import { UserProfile } from '@/types';
import { KarmaEngine } from './karmaEngine';

export interface PersonaRewriteOptions {
  karmaContext?: {
    recentHistory: string[];
    playerLevel: number;
    location: string;
  };
  nsfw18Plus?: boolean; // respect 18+ tone while still applying light censorship
}

/**
 * Persona-driven chat rewriter.
 * - Styles player text according to their profile (traits, mindset) and karma.
 * - Applies light censorship for slurs/harassment (replaces with softer synonyms or asterisks).
 * - Keeps messages concise and in-universe.
 */
export const personaRewriteService = {
  rewrite(input: string, profile: UserProfile | null | undefined, opts: PersonaRewriteOptions = {}): string {
    const text = sanitizeWhitespace(input || '');
    if (!text) return '';

    const karma = profile?.stats?.karma ?? 0;
    const traits = (profile?.traits || []).map(t => t.name.toLowerCase());
    const mindset = profile?.mindset?.name?.toLowerCase() || '';

    // Tone selection based on karma balance
    const tone = karma > 200 ? 'noble' : karma > 50 ? 'optimistic' : karma < -200 ? 'ruthless' : karma < -50 ? 'cynical' : 'neutral';

    // Style lexicon from traits/mindset
    const lex = buildLexicon(traits, mindset);

    // Light censorship pass
    const censored = lightCensor(text, !!opts.nsfw18Plus);

    // Karma context modifier (affects verbosity and confidence)
    const ctx = opts.karmaContext || { recentHistory: [], playerLevel: 1, location: 'unknown' };
    const impact = KarmaEngine.calculateKarmaMomentum(ctx.recentHistory || []);
    const confidence = impact > 5 ? 'confident' : impact < -5 ? 'guarded' : 'calm';

    // Assemble style
    const prefix = stylePrefix(tone, confidence, lex);
    const styled = applyLexicalFlavor(censored, lex);

    // Keep concise, immersive
    return clampLength(`${prefix}${styled}`.trim(), 280);
  }
};

function sanitizeWhitespace(s: string): string { return s.replace(/\s+/g, ' ').trim(); }

function clampLength(s: string, max: number): string { return s.length > max ? s.slice(0, max - 1) + '…' : s; }

function lightCensor(s: string, nsfw: boolean): string {
  // Minimal filter: mask common profanity/slurs; allow adult tone otherwise
  const map: Array<[RegExp, string]> = [
    [/\b(fuck|shit|bitch)\b/gi, '****'],
    [/\b(asshole)\b/gi, 'jerk'],
  ];
  let out = s;
  map.forEach(([re, rep]) => { out = out.replace(re, rep); });
  return out;
}

function buildLexicon(traits: string[], mindset: string) {
  const cyber = ['neon', 'datastream', 'grid', 'synth', 'chrome'];
  const warrior = ['blade', 'strike', 'honor'];
  const scholar = ['theorem', 'axiom', 'lattice'];
  const trickster = ['wink', 'sleight', 'riddle'];
  const oracle = ['omen', 'weave', 'echo'];

  const flavor: string[] = [...cyber];
  if (traits.some(t => t.includes('warrior'))) flavor.push(...warrior);
  if (traits.some(t => t.includes('scholar'))) flavor.push(...scholar);
  if (traits.some(t => t.includes('trickster'))) flavor.push(...trickster);
  if (traits.some(t => t.includes('oracle'))) flavor.push(...oracle);
  if (mindset.includes('stoic')) flavor.push('stoic');
  if (mindset.includes('chaotic')) flavor.push('chaotic');
  return flavor;
}

function stylePrefix(tone: string, confidence: string, lex: string[]): string {
  const flair = lex.length ? lex[Math.floor(Math.random() * lex.length)] : 'signal';
  switch (tone) {
    case 'noble': return `【${flair}】(steady ${confidence}) `;
    case 'optimistic': return `【${flair}】(bright ${confidence}) `;
    case 'ruthless': return `【${flair}】(cold ${confidence}) `;
    case 'cynical': return `【${flair}】(dry ${confidence}) `;
    default: return `【${flair}】 `;
  }
}

function applyLexicalFlavor(s: string, lex: string[]) {
  if (!lex.length) return s;
  // Replace a few mundane words with flavored jargon occasionally
  return s
    .replace(/city/gi, () => pick(['grid', 'neon-tier']))
    .replace(/say/gi, () => pick(['signal', 'ping']))
    .replace(/look/gi, () => pick(['scan', 'sweep']))
    .replace(/friend/gi, () => pick(['ally', 'link']))
}

function pick<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }

